<script setup>
import { defineProps, defineEmits, onMounted, watch, ref } from 'vue';
import { Doughnut } from 'vue-chartjs';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

// Enregistrement des composants Chart.js nécessaires
ChartJS.register(ArcElement, Tooltip, Legend);

// Définition des props
const props = defineProps({
  chartData: {
    type: Object,
    required: true
  },
  chartOptions: {
    type: Object,
    default: () => ({})
  },
  height: {
    type: Number,
    default: 300
  },
  width: {
    type: Number,
    default: null
  }
});

// Référence au composant Chart
const chartRef = ref(null);

// Palette de couleurs OpSolver
const opsolverColors = [
  '#30C7B5', // Turquoise principal
  '#5DD6C7', // Turquoise clair
  '#26A194', // Turquoise foncé
  '#1D7A70', // Turquoise très foncé
  '#FF7A68', // Corail doux
  '#2B8FA6', // Bleu marine
  '#27B577', // Vert émeraude
  '#A9EAE1', // Turquoise très clair
  '#155751'  // Turquoise profond
];

// Options par défaut pour le graphique
const defaultOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'right',
      labels: {
        font: {
          family: "'Poppins', sans-serif",
          size: 12
        },
        usePointStyle: true,
        padding: 20
      }
    },
    tooltip: {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      titleColor: '#333',
      bodyColor: '#666',
      bodyFont: {
        family: "'Poppins', sans-serif"
      },
      titleFont: {
        family: "'Poppins', sans-serif",
        weight: 'bold'
      },
      borderColor: '#ddd',
      borderWidth: 1,
      padding: 10,
      boxPadding: 5,
      usePointStyle: true,
      callbacks: {
        labelTextColor: () => '#333'
      }
    }
  },
  cutout: '65%',
  borderWidth: 2,
  borderColor: '#fff'
};

// Fusion des options par défaut avec les options passées en props
const chartOptions = {
  ...defaultOptions,
  ...props.chartOptions
};

// Surveiller les changements de données pour mettre à jour le graphique
watch(() => props.chartData, (newData) => {
  if (chartRef.value && chartRef.value.chart) {
    chartRef.value.chart.data = newData;
    chartRef.value.chart.update();
  }
}, { deep: true });

// S'assurer que les couleurs OpSolver sont utilisées
onMounted(() => {
  if (chartRef.value && chartRef.value.chart) {
    // Appliquer les couleurs OpSolver si aucune couleur n'est définie
    if (!props.chartData.datasets[0].backgroundColor) {
      chartRef.value.chart.data.datasets[0].backgroundColor = opsolverColors;
      chartRef.value.chart.update();
    }
  }
});
</script>

<template>
  <div class="chart-wrapper" :style="{ height: `${height}px`, width: width ? `${width}px` : '100%' }">
    <Doughnut 
      ref="chartRef"
      :data="chartData"
      :options="chartOptions"
    />
  </div>
</template>

<style scoped>
.chart-wrapper {
  position: relative;
  margin: auto;
}
</style>
